import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, UserCog } from "lucide-react";

export default function Staff() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/auth/staff")).data });

  const add = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields required");
    try {
      await api.post("/auth/staff", form); toast.success("Staff member added"); setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff" });
      qc.invalidateQueries({ queryKey: ["staff"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (s) => {
    try { await api.delete(`/auth/staff/${s.id}`); toast.success("Removed"); qc.invalidateQueries({ queryKey: ["staff"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Staff & Roles" subtitle="Manage team access">
        <Button onClick={() => setOpen(true)} data-testid="add-staff-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> Add Staff</Button>
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {staff.map((s) => (
          <div key={s.id} className="kul-card p-4 flex items-center gap-3" data-testid={`staff-${s.id}`}>
            <div className="w-11 h-11 rounded-full bg-[var(--kul-primary)]/10 text-[var(--kul-primary)] flex items-center justify-center font-semibold text-lg">{s.name?.[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{s.name}</div>
              <div className="text-xs text-neutral-400 truncate">{s.email}</div>
              <Badge className={`mt-1 capitalize ${s.role === "admin" ? "bg-[var(--kul-primary)]/15 text-[var(--kul-primary)]" : "bg-neutral-100 text-neutral-600"}`}>{s.role}</Badge>
            </div>
            {s.role !== "admin" && <button onClick={() => del(s)} className="text-neutral-300 hover:text-[var(--kul-danger)]" data-testid={`del-staff-${s.id}`}><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" /></div>
            <div><Label>Role</Label><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="staff-role">
              <option value="staff">Cashier / Staff</option>
              <option value="waiter">Waiter</option>
              <option value="kitchen">Kitchen</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select></div>
          </div>
          <DialogFooter><Button onClick={add} className="bg-[var(--kul-primary)] text-white" data-testid="save-staff">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
